'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'

import { useSession } from 'next-auth/react'

import { getEffectivePermissions } from '@/libs/permissionsConfig'
import type { PermissionString, RoleType } from '@/types/apps/roleTypes'

interface UsePermissionsOutput {
  hasPermission: (permission: PermissionString) => boolean
  isLoading: boolean
  effectivePermissions: Set<PermissionString>
  userRoles: RoleType[] | undefined | null
}

export const usePermissions = (): UsePermissionsOutput => {
  const { data: session, status } = useSession()
  const isLoadingSession = status === 'loading'

  // Track if permissions have been initialized (i.e., session has loaded)
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    // Mark as initialized once the session status is resolved (not loading)
    if (status !== 'loading') {
      setIsInitialized(true)
    }
  }, [status]) // Depends only on session status

  const userRoles = useMemo(() => {
    if (status === 'authenticated' && session?.user?.roles) {
      return session.user.roles
    }

    
return null
  }, [session, status])

  const effectivePermissions = useMemo(() => {
    return getEffectivePermissions(userRoles)
  }, [userRoles])

  const hasPermission = useCallback(
    (permission: PermissionString): boolean => {
      // Return false if session is still loading or the hook hasn't been initialized
      if (isLoadingSession || !isInitialized) {
        return false
      }

      // Directly check against the memoized effectivePermissions set
      return effectivePermissions.has(permission)
    },
    [isLoadingSession, isInitialized, effectivePermissions]
  ) // Dependencies for useCallback

  return {
    hasPermission,

    // isLoading is true if the session is loading OR the hook hasn't initialized yet
    isLoading: isLoadingSession || !isInitialized,
    effectivePermissions,
    userRoles
  }
}
